"use client";

import { useRef, useCallback, useEffect, useState, useMemo } from "react";
import {
  type LangChainMessage,
  type LangGraphMessagesEvent,
} from "@assistant-ui/react-langgraph";
import {
  useExternalStoreRuntime,
  useExternalMessageConverter,
} from "@assistant-ui/react";
import ApiService from "./api.service";
import { getChatThreadMessages, invalidateChatThreads, compactThread } from "./data.store";

interface LangGraphRuntimeConfig {
  agentType: 'build_chart' | 'onboarding' | 'analyst' | 'data_architect' | 'use_case_discovery' | 'eda' | 'data_modeling' | 'ellie';
  model?: string;
  threadId: string;
  sourceId?: string;
  dataSourceId?: string;
  useCaseId?: string;
}

// Convert LangChain messages to assistant-ui format
function convertLangChainMessages(messages: LangChainMessage[]) {
  // First, build a map of tool_call_id -> tool result
  const toolResults = new Map<string, { content: string; name: string; status?: any }>();
  for (const msg of messages) {
    if (msg.type === 'tool') {
      const toolMsg = msg as any;
      // Include tool messages even if content is empty (for running state)
      // The status will indicate whether it's running or complete
      const toolCallId = toolMsg.tool_call_id;
      if (toolCallId) {
        toolResults.set(toolCallId, {
          content: toolMsg.content !== undefined 
            ? (typeof toolMsg.content === 'string' ? toolMsg.content : JSON.stringify(toolMsg.content))
            : '',
          name: toolMsg.name || 'unknown',
          status: toolMsg.status, // Explicit status from backend (e.g., {type: "running"} or {type: "complete"})
        });
      }
    }
  }

  // Now convert messages, skipping tool messages (they'll be merged into tool-calls)
  return messages.flatMap((msg): any[] => {
    if (msg.type === 'human') {
      return [{
        role: 'user' as const,
        id: msg.id || crypto.randomUUID(),
        content: [{ type: 'text' as const, text: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content) }],
      }];
    } else if (msg.type === 'ai') {
      const parts: any[] = [];

      // Add text content
      if (msg.content) {
        const textContent = typeof msg.content === 'string' ? msg.content :
          Array.isArray(msg.content) ? msg.content.map(c => typeof c === 'string' ? c : (c as any).text || '').join('') : '';
        if (textContent) {
          parts.push({ type: 'text' as const, text: textContent });
        }
      }

      // Add tool calls with their results
      if ((msg as any).tool_calls) {
        for (const tc of (msg as any).tool_calls) {
          const result = toolResults.get(tc.id);
          parts.push({
            type: 'tool-call' as const,
            toolCallId: tc.id,
            toolName: tc.name,
            args: tc.args,
            argsText: tc.argsText || JSON.stringify(tc.args, null, 2),
            // Include result if available
            ...(result && { result: result.content }),
            // Include status if available (for error detection)
            ...(result?.status && { status: result.status }),
          });
        }
      }

      return [{
        role: 'assistant' as const,
        id: msg.id || crypto.randomUUID(),
        content: parts.length > 0 ? parts : [{ type: 'text' as const, text: '' }],
      }];
    }
    // Skip tool messages - they're merged into the AI message's tool-calls
    return [];
  });
}

/**
 * Custom hook that creates a LangGraph-compatible runtime for assistant-ui.
 * This directly uses useExternalStoreRuntime for more control over the message state.
 */
export function useCustomLangGraphRuntime(config: LangGraphRuntimeConfig) {
  const threadIdRef = useRef<string>(config.threadId);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [messages, setMessages] = useState<LangChainMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHistoricalThread, setIsHistoricalThread] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Update threadIdRef when config.threadId changes
  useEffect(() => {
    threadIdRef.current = config.threadId;
  }, [config.threadId]);

  // Load messages when component mounts (for existing threads)
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const result = await getChatThreadMessages(config.threadId);
        const backendMessages = result.messages;

        if (backendMessages.length > 0) {
          // This is a historical thread with existing messages
          setIsHistoricalThread(true);

          // Convert backend messages to LangChain format
          const langChainMessages: LangChainMessage[] = backendMessages.map(msg => {
            if (msg.role === 'user') {
              return {
                id: msg.id,
                type: 'human' as const,
                content: msg.content,
              };
            } else if (msg.role === 'assistant') {
              const lcMsg: LangChainMessage = {
                id: msg.id,
                type: 'ai' as const,
                content: msg.content || '',
              };
              if (msg.tool_calls && msg.tool_calls.length > 0) {
                (lcMsg as any).tool_calls = msg.tool_calls.map((tc: any) => ({
                  id: tc.id,
                  name: tc.name,
                  args: tc.args,
                }));
              }
              return lcMsg;
            } else {
              // Tool message - include status if present
              const toolMsg: any = {
                id: msg.id,
                type: 'tool' as const,
                content: msg.content,
                tool_call_id: msg.tool_call_id || '',
                name: msg.name || '',
              };
              // Include status if present (for tool call completion status)
              if ((msg as any).status !== undefined) {
                toolMsg.status = (msg as any).status;
              }
              return toolMsg as LangChainMessage;
            }
          });

          setMessages(langChainMessages);
        }
      } catch (error: any) {
        // 404 is expected for new threads that don't exist yet
        // The thread will be created automatically when the first message is sent
        if (error?.code === 404) {
          console.debug('[LangGraphRuntime] Thread does not exist yet (new thread):', config.threadId);
        } else {
          console.error('[LangGraphRuntime] Failed to load messages:', error);
        }
      } finally {
        setIsLoaded(true);
      }
    };

    loadMessages();
  }, [config.threadId]);

  // Convert messages to assistant-ui format
  const rawConvertedMessages = useExternalMessageConverter({
    callback: convertLangChainMessages,
    messages: [messages],
    isRunning,
  });

  // Add error status to the last assistant message if there's an error
  const convertedMessages = useMemo(() => {
    if (!error || rawConvertedMessages.length === 0) {
      return rawConvertedMessages;
    }

    const messagesWithError = [...rawConvertedMessages];
    for (let i = messagesWithError.length - 1; i >= 0; i--) {
      if (messagesWithError[i].role === 'assistant') {
        messagesWithError[i] = {
          ...messagesWithError[i],
          status: {
            type: 'incomplete' as const,
            reason: 'error' as const,
            error: error instanceof Error ? error.message : String(error),
          },
        };
        break;
      }
    }
    return messagesWithError;
  }, [rawConvertedMessages, error]);

  // Helper to send messages to the backend and stream responses
  const streamResponse = useCallback(async (messagesToSend: LangChainMessage[], options?: { branchFromPosition?: number; command?: { resume: any } }) => {
    const isResume = !!options?.command;
    console.log('[LangGraphRuntime] streamResponse called with', messagesToSend.length, 'messages, options:', options, 'isResume:', isResume);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    let requestBody: any;

    if (isResume) {
      // Resume from interrupt - send command instead of messages
      requestBody = {
        command: options!.command,
        stream: true,
        agent_type: config.agentType,
        thread_id: threadIdRef.current,
        model: config.model,
      };
    } else {
      // Normal message - send the last user message
      const lastUserMsg = [...messagesToSend].reverse().find(msg => msg.type === 'human');
      console.log('[LangGraphRuntime] lastUserMsg:', lastUserMsg);

      const userMessages = lastUserMsg ? [{
        role: 'user',
        content: typeof lastUserMsg.content === 'string' ? lastUserMsg.content : ''
      }] : [];

      if (userMessages.length === 0) {
        console.warn('[LangGraphRuntime] No user messages to send!');
        return;
      }

      requestBody = {
        messages: userMessages,
        stream: true,
        agent_type: config.agentType,
        thread_id: threadIdRef.current,
        model: config.model,
        ...(options?.branchFromPosition && { branch_from_position: options.branchFromPosition }),
        ...(config.sourceId && { source_id: config.sourceId }),
        ...(config.dataSourceId && { data_source_id: config.dataSourceId }),
        ...(config.useCaseId && { use_case_id: config.useCaseId }),
      };
    }

    console.log('[LangGraphRuntime] streamResponse: Sending request to backend:', {
      url: '/chat/completions/langgraph/v2',
      method: 'POST',
      body: requestBody,
      threadId: threadIdRef.current,
      agentType: config.agentType,
      model: config.model,
    });

    const streamGen = ApiService.getInstance().requestStream('POST', '/chat/completions/langgraph/v2', requestBody, controller.signal);

    for await (const event of streamGen) {
      // Handle error events from the backend
      if ((event as any).type === 'error') {
        setError(new Error((event as any).error));
        setMessages(prev => {
          const hasAiMessage = prev.some(m => m.type === 'ai');
          if (hasAiMessage) {
            return prev;
          }
          const lastUserIndex = prev.map(m => m.type).lastIndexOf('human');
          const messagesBeforeResponse = lastUserIndex >= 0 ? prev.slice(0, lastUserIndex + 1) : prev;
          return [...messagesBeforeResponse, {
            id: crypto.randomUUID(),
            type: 'ai' as const,
            content: '',
          }];
        });
        return;
      }

      const typedEvent = event as any;

      if (typedEvent.event === 'text/delta') {
        // Append text delta to the AI message
        const { message_id, delta } = typedEvent.data;
        setMessages(prev => {
          const msgs = [...prev];
          const aiMsgIdx = msgs.findIndex(m => m.id === message_id);
          if (aiMsgIdx >= 0) {
            const aiMsg = msgs[aiMsgIdx] as any;
            msgs[aiMsgIdx] = { ...aiMsg, content: ((aiMsg.content as string) || '') + delta };
          } else {
            // New AI message — append it
            msgs.push({
              id: message_id,
              type: 'ai' as const,
              content: delta,
              tool_calls: [],
            } as any);
          }
          return msgs;
        });

      } else if (typedEvent.event === 'tool_call/begin') {
        // New tool call detected — add tool call to AI message + placeholder tool message
        const { message_id, tool_call_id, tool_name } = typedEvent.data;
        setMessages(prev => {
          // During resume, skip if the tool already exists (interrupted tool replaying)
          if (isResume) {
            const hasExistingTool = prev.some(m =>
              (m as any).type === 'tool' &&
              (m as any).name === tool_name &&
              ((m as any).status?.type === 'running' || (m as any).status?.type === 'interrupted')
            );
            if (hasExistingTool) return prev;
          }
          const msgs = [...prev];
          const aiMsgIdx = msgs.findIndex(m => m.id === message_id);
          if (aiMsgIdx >= 0) {
            const aiMsg = { ...msgs[aiMsgIdx] } as any;
            aiMsg.tool_calls = [...(aiMsg.tool_calls || []), {
              id: tool_call_id,
              name: tool_name,
              args: {},
              argsText: '',
            }];
            msgs[aiMsgIdx] = aiMsg;
          } else {
            // AI message not yet created — create it with the tool call
            msgs.push({
              id: message_id,
              type: 'ai' as const,
              content: '',
              tool_calls: [{
                id: tool_call_id,
                name: tool_name,
                args: {},
                argsText: '',
              }],
            } as any);
          }
          // Add placeholder tool result message
          msgs.push({
            id: `tool-result-${tool_call_id}`,
            type: 'tool' as const,
            content: '',
            tool_call_id: tool_call_id,
            name: tool_name,
            status: { type: 'running' },
          } as any);
          return msgs;
        });

      } else if (typedEvent.event === 'tool_call/delta') {
        // Append args JSON delta to tool call
        const { message_id, tool_call_id, delta } = typedEvent.data;
        setMessages(prev => {
          const msgs = [...prev];
          const aiMsgIdx = msgs.findIndex(m => m.id === message_id);
          if (aiMsgIdx >= 0) {
            const aiMsg = { ...msgs[aiMsgIdx] } as any;
            const toolCalls = [...(aiMsg.tool_calls || [])];
            const tcIdx = toolCalls.findIndex((tc: any) => tc.id === tool_call_id);
            if (tcIdx >= 0) {
              const tc = { ...toolCalls[tcIdx] };
              tc.argsText = (tc.argsText || '') + delta;
              try {
                tc.args = JSON.parse(tc.argsText);
              } catch {
                // Still partial JSON, that's OK — argsText will be displayed as-is
              }
              toolCalls[tcIdx] = tc;
            }
            aiMsg.tool_calls = toolCalls;
            msgs[aiMsgIdx] = aiMsg;
          }
          return msgs;
        });

      } else if (typedEvent.event === 'tool_call/args_done') {
        // Args finalized — replace with parsed args
        const { message_id, tool_call_id, args } = typedEvent.data;
        setMessages(prev => {
          const msgs = [...prev];
          const aiMsgIdx = msgs.findIndex(m => m.id === message_id);
          if (aiMsgIdx >= 0) {
            const aiMsg = { ...msgs[aiMsgIdx] } as any;
            const toolCalls = [...(aiMsg.tool_calls || [])];
            const tcIdx = toolCalls.findIndex((tc: any) => tc.id === tool_call_id);
            if (tcIdx >= 0) {
              toolCalls[tcIdx] = { ...toolCalls[tcIdx], args, argsText: JSON.stringify(args, null, 2) };
            }
            aiMsg.tool_calls = toolCalls;
            msgs[aiMsgIdx] = aiMsg;
          }
          return msgs;
        });

      } else if (typedEvent.event === 'tool/complete') {
        // Tool execution finished — update tool message with result
        const { tool_call_id, content, name: toolName, status } = typedEvent.data;
        setMessages(prev => {
          const msgs = [...prev];
          let toolMsgIdx = msgs.findIndex(m =>
            (m as any).type === 'tool' && (m as any).tool_call_id === tool_call_id
          );
          // Fallback: match by name + running/interrupted status (for resume ID mismatches)
          if (toolMsgIdx < 0 && toolName) {
            toolMsgIdx = msgs.findIndex(m =>
              (m as any).type === 'tool' &&
              (m as any).name === toolName &&
              ((m as any).status?.type === 'running' || (m as any).status?.type === 'interrupted')
            );
          }
          if (toolMsgIdx >= 0) {
            msgs[toolMsgIdx] = {
              ...msgs[toolMsgIdx],
              content: content,
              status: status || { type: 'complete' },
            } as any;
          }
          return msgs;
        });

      } else if (typedEvent.event === 'messages/complete') {
        if (isResume) {
          // During resume, granular events already updated existing messages.
          // Skip the full replace to preserve the original AI message text.
          continue;
        }
        // Final sync — replace entire response section with complete state
        setMessages(prev => {
          let lastUserIndex = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].type === 'human') {
              lastUserIndex = i;
              break;
            }
          }
          const messagesBeforeResponse = lastUserIndex >= 0 ? prev.slice(0, lastUserIndex + 1) : prev;
          return [...messagesBeforeResponse, ...typedEvent.data];
        });

      } else if (typedEvent.event === 'messages/partial') {
        // Fallback for backward compat
        setMessages(prev => {
          let lastUserIndex = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].type === 'human') {
              lastUserIndex = i;
              break;
            }
          }
          const messagesBeforeResponse = lastUserIndex >= 0 ? prev.slice(0, lastUserIndex + 1) : prev;
          const currentResponseMessages = prev.slice(lastUserIndex + 1);
          const newMessages = typedEvent.data;
          const updatedResponseMessages = [...currentResponseMessages];
          for (const newMsg of newMessages) {
            const existingIndex = updatedResponseMessages.findIndex(m => m.id === newMsg.id);
            if (existingIndex >= 0) {
              updatedResponseMessages[existingIndex] = { ...updatedResponseMessages[existingIndex], ...newMsg };
            } else {
              updatedResponseMessages.push(newMsg);
            }
          }
          return [...messagesBeforeResponse, ...updatedResponseMessages];
        });

      } else if (typedEvent.event === 'metadata') {
        // Log metadata but no need to track on frontend
      }
    }
  }, [config.agentType, config.model]);

  // Handle sending new messages
  const onNew = useCallback(async (message: any) => {
    console.log('[LangGraphRuntime] onNew called with message:', message);
    const textContent = message.content
      .filter((c: any) => c.type === 'text')
      .map((c: any) => c.text)
      .join(' ');

    if (!textContent || textContent.trim() === '') {
      console.warn('[LangGraphRuntime] Empty message content, skipping');
      return;
    }

    // Add user message to state
    const userMessage: LangChainMessage = {
      id: crypto.randomUUID(),
      type: 'human',
      content: textContent,
    };

    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      console.log('[LangGraphRuntime] onNew: setting messages, count:', newMessages.length);
      return newMessages;
    });
    setIsRunning(true);
    setError(undefined); // Clear any previous error

    // Track if this is the first message (new chat)
    const isFirstMessage = messages.length === 0;

    try {
      console.log('[LangGraphRuntime] onNew: calling streamResponse with', messages.length + 1, 'messages');
      await streamResponse([...messages, userMessage]);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('[LangGraphRuntime] Stream error:', error);
      setError(error instanceof Error ? error : new Error(String(error)));
      // Ensure there's an AI message for error display if stream failed before any AI response
      setMessages(prev => {
        const hasAiMessage = prev.some(m => m.type === 'ai');
        if (hasAiMessage) {
          return prev; // Keep existing messages
        }
        // Add empty AI message for error display
        return [...prev, {
          id: crypto.randomUUID(),
          type: 'ai' as const,
          content: '',
        }];
      });
    } finally {
      setIsRunning(false);
      // Refresh chat threads list only for new chats
      if (isFirstMessage) {
        invalidateChatThreads();
      }
    }
  }, [messages, streamResponse]);

  // Handle editing a user message and regenerating the response
  const onEdit = useCallback(async (message: any) => {
    console.log('[LangGraphRuntime] onEdit called');

    const textContent = message.content
      .filter((c: any) => c.type === 'text')
      .map((c: any) => c.text)
      .join(' ');

    const parentId = message.parentId;
    console.log('[LangGraphRuntime] Edit: parentId', parentId, 'textContent:', textContent);

    // Find the position of the message being edited
    let messageIndex = -1;
    for (let i = 0; i < convertedMessages.length; i++) {
      if (convertedMessages[i].id === parentId) {
        messageIndex = i;
        break;
      }
    }
    console.log('[LangGraphRuntime] Edit: messageIndex', messageIndex);

    // Count user messages up to and including the parent
    let userCountBeforeEdit = 0;
    if (messageIndex >= 0) {
      for (let i = 0; i <= messageIndex; i++) {
        if (convertedMessages[i].role === 'user') {
          userCountBeforeEdit++;
        }
      }
    }

    // The position of the user message being EDITED is one more than the count before it
    const editPosition = userCountBeforeEdit + 1;
    console.log('[LangGraphRuntime] Edit: userCountBeforeEdit', userCountBeforeEdit, 'editPosition', editPosition);

    // Keep LangChain messages up to (but not including) the user message being edited
    let messagesToKeep: LangChainMessage[] = [];
    if (editPosition > 1) {
      let count = 0;
      for (const msg of messages) {
        if (msg.type === 'human') {
          count++;
          if (count >= editPosition) break; // Stop before the message being edited
        }
        messagesToKeep.push(msg);
      }
    }

    const editedMessage: LangChainMessage = {
      id: crypto.randomUUID(),
      type: 'human',
      content: textContent,
    };

    const newMessages = [...messagesToKeep, editedMessage];
    console.log('[LangGraphRuntime] Edit: newMessages count:', newMessages.length, 'branchFromPosition:', editPosition);
    setMessages(newMessages);
    setIsRunning(true);

    try {
      console.log('[LangGraphRuntime] Edit: calling streamResponse...');
      await streamResponse(newMessages, { branchFromPosition: editPosition });
      console.log('[LangGraphRuntime] Edit: streamResponse completed');
    } catch (error) {
      console.error('[LangGraphRuntime] Edit error:', error);
      setError(error instanceof Error ? error : new Error(String(error)));
      // Ensure there's an AI message for error display
      setMessages(prev => {
        const hasAiMessage = prev.some(m => m.type === 'ai');
        if (hasAiMessage) {
          return prev;
        }
        return [...prev, {
          id: crypto.randomUUID(),
          type: 'ai' as const,
          content: '',
        }];
      });
    } finally {
      setIsRunning(false);
    }
  }, [messages, convertedMessages, streamResponse]);

  // Handle regenerating the assistant response from a specific parent message
  const onReload = useCallback(async (parentId: string | null) => {
    console.log('[LangGraphRuntime] onReload called with parentId:', parentId);

    let messagesToKeep: LangChainMessage[] = [];
    let userMsgPosition = 0;

    if (parentId === null) {
      // Regenerate from the very beginning - just keep the first user message
      const firstUserMsg = messages.find(m => m.type === 'human');
      if (firstUserMsg) {
        messagesToKeep = [firstUserMsg];
        userMsgPosition = 1;
      }
    } else {
      // Find the parent message position in convertedMessages
      let foundParentIdx = -1;
      for (let i = 0; i < convertedMessages.length; i++) {
        if (convertedMessages[i].id === parentId) {
          foundParentIdx = i;
          break;
        }
      }

      if (foundParentIdx >= 0) {
        // Count which user message position this is
        for (let i = 0; i <= foundParentIdx; i++) {
          if (convertedMessages[i].role === 'user') {
            userMsgPosition++;
          }
        }

        // Keep LangChain messages up to and including this user message
        let count = 0;
        for (const msg of messages) {
          messagesToKeep.push(msg);
          if (msg.type === 'human') {
            count++;
            if (count >= userMsgPosition) break;
          }
        }
      } else {
        // Fallback: keep up to the last user message
        let lastUserIdx = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].type === 'human') {
            lastUserIdx = i;
            userMsgPosition++;
            break;
          }
        }
        if (lastUserIdx >= 0) {
          messagesToKeep = messages.slice(0, lastUserIdx + 1);
        }
      }
    }

    if (messagesToKeep.length === 0) {
      console.warn('[LangGraphRuntime] No messages to reload from');
      return;
    }

    console.log('[LangGraphRuntime] Reload: position', userMsgPosition, 'messagesToKeep count:', messagesToKeep.length);
    setMessages(messagesToKeep);
    setIsRunning(true);

    try {
      console.log('[LangGraphRuntime] Reload: calling streamResponse...');
      await streamResponse(messagesToKeep, { branchFromPosition: userMsgPosition });
      console.log('[LangGraphRuntime] Reload: streamResponse completed');
    } catch (error) {
      console.error('[LangGraphRuntime] Reload error:', error);
      setError(error instanceof Error ? error : new Error(String(error)));
      // Ensure there's an AI message for error display
      setMessages(prev => {
        const hasAiMessage = prev.some(m => m.type === 'ai');
        if (hasAiMessage) {
          return prev;
        }
        return [...prev, {
          id: crypto.randomUUID(),
          type: 'ai' as const,
          content: '',
        }];
      });
    } finally {
      setIsRunning(false);
    }
  }, [messages, convertedMessages, streamResponse]);

  // Resume the graph from an interrupt (e.g., after file upload completes)
  const resumeGraph = useCallback(async (resumeValue: any) => {
    console.log('[LangGraphRuntime] resumeGraph called with:', resumeValue);
    setIsRunning(true);
    setError(undefined);

    try {
      await streamResponse([], { command: { resume: resumeValue } });
    } catch (error) {
      console.error('[LangGraphRuntime] Resume error:', error);
      setError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsRunning(false);
    }
  }, [streamResponse]);

  // Cancel the current stream
  const onCancel = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Mark any running tool messages as cancelled
    setMessages(prev => {
      let changed = false;
      const updated = prev.map(m => {
        if ((m as any).type === 'tool' && (m as any).status?.type === 'running') {
          changed = true;
          return { ...m, content: 'Tool call was cancelled by the user.', status: { type: 'error' } } as any;
        }
        return m;
      });
      return changed ? updated : prev;
    });
    setIsRunning(false);
  }, []);

  // Compact the conversation by summarizing older messages
  const compactConversation = useCallback(async () => {
    const result = await compactThread(threadIdRef.current);

    const langChainMessages: LangChainMessage[] = result.messages.map((msg: any) => {
      if (msg.role === 'user') {
        return { id: msg.id, type: 'human' as const, content: msg.content };
      } else if (msg.role === 'assistant') {
        const lcMsg: any = { id: msg.id, type: 'ai' as const, content: msg.content || '' };
        if (msg.tool_calls && msg.tool_calls.length > 0) {
          lcMsg.tool_calls = msg.tool_calls.map((tc: any) => ({
            id: tc.id, name: tc.name, args: tc.args,
          }));
        }
        return lcMsg as LangChainMessage;
      } else {
        return {
          id: msg.id, type: 'tool' as const, content: msg.content,
          tool_call_id: msg.tool_call_id || '', name: msg.name || '',
        } as LangChainMessage;
      }
    });

    setMessages(langChainMessages);
    return result;
  }, []);

  const runtime = useExternalStoreRuntime({
    isRunning,
    messages: convertedMessages,
    onNew,
    onEdit,
    onReload,
    onCancel,
    unstable_capabilities: {
      copy: true,
    },
  });

  return {
    runtime,
    isLoaded,
    isHistoricalThread,
    getThreadId: () => threadIdRef.current,
    error,
    resumeGraph,
    compactConversation,
    messageCount: messages.length,
  };
}

